import {
  ColumnFactory,
  createIconCellFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  RemoteTypeMappings,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { FieldOrderField, RemoteType } from '../../../gen-src/queries/globalTypes';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import { createQueryPreservingReplacingLinkCellFormatter } from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { RemoteTypeIcon } from '../datagrid/cellFormatters/RemoteTypeIcon';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { FieldIcon } from '../field/types/FieldIcon';
import { FieldsValidSortFields } from '../sorting/sortProviders/FieldsGridSortProvider';

export interface FlowFieldsDisplayGridRowData {
  readonly flowVizportalUrlId: string;
  readonly fieldIcon: FieldIcon | null;
  readonly remoteType?: RemoteType; // Optional remote type to support flow fields that output to columns
  readonly name: string | null;
  readonly id: string;
  readonly sheetCount: number | null;
}

type FlowFieldsColumnFactory = ColumnFactory<
  GridSortProps<FieldsValidSortFields>,
  FlowFieldsDisplayGridRowData,
  FieldsValidSortFields
>;

const columns: ReadonlyArray<FlowFieldsColumnFactory> = [
  () => createSelectionColumn(),
  (sortParams) => ({
    name: 'type',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderType(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createIconCellFormatter(
      ({ fieldIcon, remoteType }) =>
        remoteType ? <RemoteTypeIcon remoteType={remoteType} /> : <SvgFieldIcon fieldIcon={fieldIcon} />,
      ({ fieldIcon, remoteType }) =>
        remoteType ? RemoteTypeMappings[remoteType]() : fieldIcon ? FieldNameMappings[fieldIcon]() : ''
    ),
    sortable: false,
    resizable: false
  }),
  (sortParams) => ({
    name: 'name',
    dataKey: FieldOrderField.NAME,
    headerText: Messages.GridHeaderName(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createQueryPreservingReplacingLinkCellFormatter(
      ({ name }) => name || '',
      (urls, { id, flowVizportalUrlId }) => urls.flowLineageFieldPath(flowVizportalUrlId, id),
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'sheets',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderSheets(),
    headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
    cellFormatter: createNumberCellFormatter(({ sheetCount }) => sheetCount),
    sortable: false,
  })
];

export type FlowFieldsDisplayGridProps =
  UsualDisplayGridProps<FlowFieldsDisplayGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<FieldsValidSortFields>;

export function FlowFieldsDisplayGrid(props: FlowFieldsDisplayGridProps): JSX.Element {
  const cols = columns.map(columnFactory => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
